// Bottom-of-chat input. Multi-line textarea that grows with content,
// Send button, keyboard shortcuts: Enter to send, Shift+Enter for
// newline. Disabled while a stream is in flight (send button flips
// to a Stop button that aborts the current request).
//
// 2026-10-15 — ephemeral image attachments. File picker + drag/drop
// zone; thumbnails preview above the input with a remove button per
// attachment. Max 4 attachments per message, 5MB each, image MIMEs
// only — client-side limits mirror the backend validator so a bad
// file is caught before the POST.

import { useEffect, useRef, useState } from 'react';
import {
  Box, HStack, Textarea, IconButton, Tooltip, Image, Text, useToast, VStack,
} from '@chakra-ui/react';
import { ArrowUp, X, Paperclip } from 'lucide-react';
import type { ChatAttachment, ChatAttachmentPreview } from './types';

type Props = {
  value:       string;
  onChange:    (v: string) => void;
  attachments: ChatAttachment[];
  previews:    ChatAttachmentPreview[];
  onAttachmentsChange: (atts: ChatAttachment[], previews: ChatAttachmentPreview[]) => void;
  onSend:      () => void;
  onAbort?:    () => void;
  disabled?:   boolean;   // while pending; UI shows the stop button
  placeholder?: string;
  busy?:       boolean;   // true while stream is in flight
};

const MAX_HEIGHT_PX = 220;
const MAX_ATTACHMENTS = 4;              // mirrors backend MAX_ATTACHMENTS_PER_MESSAGE
const MAX_BYTES = 5 * 1024 * 1024;      // mirrors backend MAX_ATTACHMENT_BASE64_BYTES
const ALLOWED_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

// Convert a File to the ChatAttachment shape backend accepts +
// generate a browser-local previewUrl for the thumbnail. Returns null
// on validation failure (caller shows a toast and skips the file).
async function fileToAttachment(
  file: File,
): Promise<{ attachment: ChatAttachment; preview: ChatAttachmentPreview } | { error: string }> {
  if (!ALLOWED_MIMES.has(file.type)) {
    return { error: `${file.name}: ${file.type || 'unknown MIME'} not accepted (image/jpeg|png|webp|gif only)` };
  }
  // Read as data URL then split off the base64 chunk. FileReader has
  // the simplest cross-browser semantics for the required encoding.
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error || new Error('read failed'));
    r.readAsDataURL(file);
  });
  const commaIdx = dataUrl.indexOf(',');
  const dataBase64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
  if (dataBase64.length > MAX_BYTES) {
    return { error: `${file.name}: ${(dataBase64.length / 1024 / 1024).toFixed(1)} MB exceeds 5 MB cap` };
  }
  const previewUrl = URL.createObjectURL(file);
  return {
    attachment: { dataBase64, mimeType: file.type, filename: file.name.slice(0, 200) },
    preview:    { previewUrl, mimeType: file.type, filename: file.name.slice(0, 200) },
  };
}

export function ChatInput({
  value, onChange, attachments, previews, onAttachmentsChange,
  onSend, onAbort, disabled, placeholder, busy,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const toast = useToast();

  // Auto-resize the textarea to fit content, up to MAX_HEIGHT_PX.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, MAX_HEIGHT_PX) + 'px';
  }, [value]);

  // Revoke previewUrls on unmount or when they're removed.
  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        try { URL.revokeObjectURL(p.previewUrl); } catch { /* best-effort */ }
      });
    };
    // Intentionally empty dep list — only revoke on unmount. The remove
    // handler revokes synchronously when the operator removes one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function addFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      toast({ title: `Max ${MAX_ATTACHMENTS} attachments per message`, status: 'warning', duration: 3000 });
      return;
    }
    const take = list.slice(0, room);
    if (list.length > room) {
      toast({ title: `Only taking first ${room} of ${list.length} (cap ${MAX_ATTACHMENTS})`, status: 'info', duration: 3000 });
    }
    const results = await Promise.all(take.map(fileToAttachment));
    const added: ChatAttachment[] = [];
    const addedPreviews: ChatAttachmentPreview[] = [];
    const errors: string[] = [];
    for (const r of results) {
      if ('error' in r) errors.push(r.error);
      else { added.push(r.attachment); addedPreviews.push(r.preview); }
    }
    if (errors.length) {
      toast({ title: 'Some files rejected', description: errors.join('; '), status: 'warning', duration: 5000 });
    }
    if (added.length) {
      onAttachmentsChange([...attachments, ...added], [...previews, ...addedPreviews]);
    }
  }

  function removeAttachment(idx: number) {
    const nextAtts = attachments.filter((_, i) => i !== idx);
    const nextPreviews = previews.filter((_, i) => i !== idx);
    const removed = previews[idx];
    if (removed) {
      try { URL.revokeObjectURL(removed.previewUrl); } catch { /* best-effort */ }
    }
    onAttachmentsChange(nextAtts, nextPreviews);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter → send; Shift+Enter → newline (default textarea behaviour).
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && (value.trim() || attachments.length > 0)) onSend();
    }
  }

  const canSend = !disabled && (value.trim().length > 0 || attachments.length > 0);

  return (
    <Box
      borderWidth="1px"
      borderColor={dragActive ? 'purple.400' : 'brand.border'}
      borderRadius="20px"
      bg={dragActive ? 'purple.50' : 'white'}
      px={3}
      py={2}
      boxShadow="sm"
      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        if (e.dataTransfer?.files?.length) void addFiles(e.dataTransfer.files);
      }}
    >
      <VStack align="stretch" spacing={2}>
        {previews.length > 0 && (
          <HStack spacing={2} flexWrap="wrap">
            {previews.map((p, i) => (
              <Box
                key={i}
                position="relative"
                w="48px"
                h="48px"
                borderRadius="sm"
                overflow="hidden"
                borderWidth="1px"
                borderColor="brand.border"
                title={p.filename}
              >
                <Image src={p.previewUrl} w="100%" h="100%" objectFit="cover" />
                <IconButton
                  aria-label="Remove attachment"
                  icon={<X size={10} />}
                  size="xs"
                  position="absolute"
                  top="2px"
                  right="2px"
                  minW="16px"
                  h="16px"
                  borderRadius="full"
                  bg="blackAlpha.700"
                  color="white"
                  _hover={{ bg: 'red.500' }}
                  onClick={() => removeAttachment(i)}
                />
              </Box>
            ))}
            <Text fontSize="10px" color="brand.muted">
              {attachments.length} / {MAX_ATTACHMENTS}
            </Text>
          </HStack>
        )}
        <HStack align="flex-end" spacing={2}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={(e) => {
              if (e.target.files) void addFiles(e.target.files);
              // Reset the input so selecting the same file twice works.
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
          <Tooltip label="Attach images">
            <IconButton
              aria-label="Attach"
              icon={<Paperclip size={14} />}
              size="sm"
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              isDisabled={disabled || attachments.length >= MAX_ATTACHMENTS}
            />
          </Tooltip>
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || 'Ask about your ads, catalog, campaigns…'}
            rows={1}
            resize="none"
            border="none"
            _focus={{ boxShadow: 'none' }}
            _hover={{ border: 'none' }}
            fontSize="14px"
            minH="24px"
            overflowY="auto"
            p={2}
          />
          {busy && onAbort
            ? (
              <Tooltip label="Stop generation">
                <IconButton
                  aria-label="Stop"
                  icon={<X size={14} />}
                  size="sm"
                  onClick={onAbort}
                  colorScheme="red"
                  variant="ghost"
                />
              </Tooltip>
            )
            : (
              <Tooltip label="Send">
                <IconButton
                  aria-label="Send"
                  icon={<ArrowUp size={16} />}
                  size="sm"
                  onClick={onSend}
                  colorScheme="brand"
                  isDisabled={!canSend}
                />
              </Tooltip>
            )
          }
        </HStack>
      </VStack>
    </Box>
  );
}
